import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import ReactSelect from "react-select";
import { AICCDateTimeInputsValue } from "./inputs/AICCDateTimeInputsValue";
import { AICCFieldInputValue } from "./inputs/AICCFieldInputValue";
import { AICCNumberInputs } from "./inputs/AICCNumberInputs";
import { AICCArithmeticSelector } from "../core/AICCArithmeticSelector";
import { AICCBuilderInitialConfig } from "constants/AICCBuilderConfig";

export default function AICCValueEditor({
  selectedField,
  selectedOperator,
  valueFieldGroup,
  value,
  onChange,
  onValueFieldGroup,
  valueSource,
  fieldList,
  useColumnGroup,
  columnsWithGroup = [],
  selectedRightArOp,
  rightArOpValue,
  onOpChange,
  onOpValueChange,
  rightLeftArithmetic,
  rightRightArithmetic,
  onCheckChanged,
  allowArithmetic,
  isTodayDateChecked,
  onChangeMultipleField,
  disabled = false,
  evaluationValues,
}) {
  const [t] = useTranslation();
  const finalColumnList = useMemo(
    () =>
      useColumnGroup
        ? (columnsWithGroup.find((e) => e.value == valueFieldGroup)
            ?.subFields ?? [])
        : fieldList,
    [fieldList, columnsWithGroup, valueFieldGroup, useColumnGroup]
  );

  if (valueSource === "field") {
    return (
      <AICCFieldInputValue
        {...{
          value,
          onChange,
          selectedOperator,
          columnsWithGroup,
          fieldList,
          finalColumnList,
          onValueFieldGroup,
          useColumnGroup,
          valueFieldGroup,
          selectedRightArOp,
          rightArOpValue,
          onOpChange,
          onOpValueChange,
          rightLeftArithmetic,
          rightRightArithmetic,
          onCheckChanged,
          allowArithmetic,
          disabled,
          evaluationValues,
        }}
      />
    );
  }

  if ((selectedOperator?.value ?? "").includes("_in")) {
    // if options is available to select then show dropdown
    if (selectedField?.fieldOptions) {
      const allFieldOptions = selectedField?.fieldOptions ?? [];

      const splittedValues = (value ?? "").split(",").map((e) => e.trim());
      const selectedOptions = allFieldOptions.filter((e) =>
        splittedValues.includes(e.value)
      );

      return (
        <div className="form-group mb-0">
          <ReactSelect
            classNamePrefix={"select"}
            value={selectedOptions}
            options={selectedField?.fieldOptions}
            isMulti
            menuPosition={"fixed"}
            placeholder={t("aiccRuleBuilder.valueAnySelectPlaceholder")}
            noOptionsLabel={t("aiccRuleBuilder.valueAnySelectNoOptions")}
            onChange={(selectedOption) => {
              if (onChange) {
                onChange((selectedOption ?? []).map((e) => e.value).join(","));
              }
            }}
            isDisabled={disabled}
          />
        </div>
      );
    }

    // if there are no options available to select then show input
    return (
      <Form.Group className="form-group mb-0">
        <Form.Control
          type={"text"}
          placeholder={t("aiccRuleBuilder.commaSeparatedInputPlaceholder")}
          value={value}
          onChange={(e) => {
            if (onChange) {
              onChange(e.target.value);
            }
          }}
          disabled={disabled}
        />
        {/* {selectedOperator?.type === "number" &&
          value &&
          !/^(([0-9]+)(,(?=[0-9]))?)+$/gm.test(value) && (
            <h6 className="text-danger mt-1 input-error-text position-absolute">
              {language?.commaSeparatedInputNumberError ??
                "Please enter valid comma separated numbers."}
            </h6>
          )} */}
      </Form.Group>
    );
  }

  if (selectedOperator?.type === "boolean") {
    return (
      <div className="switch mt-1">
        <input
          type="checkbox"
          checked={
            typeof value === "string" ? value.toLowerCase() === "true" : value
          }
          onChange={(e) => {
            if (onChange) {
              onChange(e.target.checked);
            }
          }}
          disabled={disabled}
        />
      </div>
    );
  }

  if (selectedOperator?.type === "datetime") {
    // if selected operator is between and date time is value type then show date time picker.
    return (
      <div className="d-flex" style={{ gap: ".75rem" }}>
        {(!isTodayDateChecked ||
          selectedOperator?.value?.includes("_between")) && (
          <AICCDateTimeInputsValue
            value={value}
            onChange={onChange}
            selectedOperator={selectedOperator}
            disabled={isTodayDateChecked || disabled}
          />
        )}

        {selectedOperator?.compareCurrentDate && (
          <>
            <div className="col-auto aicc-ruleBuilder__rule--today-date">
              <Form.Check
                checked={isTodayDateChecked}
                onChange={(e) => {
                  onChangeMultipleField({
                    isTodayDateChecked: e.target.checked,
                    rightLeftArithmetic: false,
                    expectedValue: "",
                    selectedRightArOp: "",
                    rightArOpValue: "",
                  });
                }}
                disabled={disabled}
              />
              <label className="form-label">
                {t("aiccRuleBuilder.compareTodayDate")}
              </label>
            </div>
            {isTodayDateChecked && (
              <div className="col-auto">
                <AICCArithmeticSelector
                  useColumnGroup={useColumnGroup}
                  columnList={finalColumnList}
                  columnsWithGroup={columnsWithGroup}
                  selectedOp={selectedRightArOp}
                  value={rightArOpValue}
                  enabled={rightLeftArithmetic}
                  disabled={disabled}
                  arithmeticOperators={
                    AICCBuilderInitialConfig.dateArithmeticOperations
                  }
                  allowValueSource={false}
                  onCheckChanged={(checked) => {
                    onCheckChanged(checked);
                  }}
                  onOpChange={(val) => {
                    onOpChange(val);
                  }}
                  onChange={(val) => {
                    onOpValueChange(val);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (
    selectedOperator?.type === "number" ||
    selectedOperator?.type === "decimal"
  ) {
    // if operator operator is between and type is number then show min max input in same line
    return (
      <AICCNumberInputs
        {...{
          value,
          onChange,
          selectedOperator,
          selectedRightArOp,
          rightArOpValue,
          onOpChange,
          onOpValueChange,
          allowArithmetic,
          disabled,
        }}
      />
    );
  }

  // if nothing is matching then show text input
  return (
    <Form.Group className="form-group mb-0">
      <Form.Control
        type={"text"}
        placeholder={t("aiccRuleBuilder.stringInputPlaceholder")}
        value={value}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        disabled={disabled}
      />
    </Form.Group>
  );
}
